<template>
  <on-click-outside :do="close">
    <div class="field-group" :class="{ 'has-error': !!error }">
      <div class="field-heading">
        <label v-if="label" @click="focus()" :class="{ 'required': required }">
          {{ label }}
        </label>
      </div>

      <div class="autocomplete" v-if="hasPermission">
        <input class="autocomplete-input" autocomplete="off"
          :style="css"
          type="text"
          :name="name"
          ref="input"
          v-model="search"
          @input="onChange()"
          @keydown.esc.prevent="close()"
          @keydown.up="highlightPrev()"
          @keydown.down="highlightNext()"
          @keydown.enter.prevent="selectHighlighted()"
          @keydown.tab.prevent
        />
        <div ref="dropdown" v-show="isOpen && results.length" class="autocomplete-dropdown">
          <ul ref="results" class="autocomplete-results">
            <li v-if="isLoading" class="autocomplete-result">Loading results...</li>
            <li v-else
              v-for="(item, index) in results"
              :key="index"
              @click="select(item)"
              class="autocomplete-result"
              :class="{ 'is-active': index === highlighted }"
            >{{ item }}</li>
          </ul>
        </div>
        <transition name="slide">
          <span v-if="error" class="block form-error text-xs">
            {{ error }}
          </span>
        </transition>
      </div>
      <div v-else class="readonly">
        <span v-if="value">{{ value }}</span>
        <span v-else>-</span>
      </div>
    </div>
  </on-click-outside>
</template>

<script>
import OnClickOutside from '@/components/globals/OnClickOutside';

export default {
  $_veeValidate: {
    name() {
      return this.label;
    },
    value() {
      return this.value;
    }
  },
  inject: ['$validator'],
  components: {
    OnClickOutside
  },
  props: {
    value: {
      required: true
    },
    label: {
      type: String,
      required: false
    },
    name: {
      type: String,
      required: true
    },
    css: {
      type: String,
      required: false
    },
    items: {
      type: Array,
      required: false,
      default: () => []
    },
    isAsync: {
      type: Boolean,
      required: false,
      default: false
    },
    error: {
      type: String,
      required: false
    },
    required: {
      type: Boolean,
      default: false
    },
    hasPermission: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      search: '',
      results: [],
      isOpen: false,
      isLoading: false,
      highlighted: -1
    };
  },
  created() {
    this.search = this.value;
  },
  methods: {
    onChange() {
      this.$emit('input', this.search);
      this.$emit('changed', this.search);

      // is the data given by an outside ajax request?
      if (this.isAsync) {
        this.isLoading = true;
      } else {
        this.filter();
      }

      this.isOpen = this.search.length >= 3;
    },
    focus() {
      this.$refs.input.focus();
    },
    open() {
      if (this.isOpen) return;

      this.isOpen = true;
    },
    close() {
      if (!this.isOpen) {
        return;
      }

      this.isOpen = false;
      this.$refs.input.focus();
    },
    filter() {
      this.results = this.items.filter(item => {
        return item.toLowerCase().includes(this.search.toLowerCase());
      });
    },
    select(item) {
      this.$emit('input', item);
      this.search = item;
      this.close();
    },
    selectHighlighted() {
      if (!this.results.length) return;

      this.select(this.results[this.highlighted]);
    },
    scrollToHighlighted() {
      this.$refs.results.children[this.highlighted].scrollIntoView({
        block: 'nearest'
      });
    },
    highlight(index) {
      if (!this.results.length) return;

      this.highlighted = index;
      const length = this.results.length - 1;

      if (this.highlighted < 0) {
        this.highlighted = length;
      }

      if (this.highlighted > length) {
        this.highlighted = 0;
      }

      this.scrollToHighlighted();
    },
    highlightPrev() {
      this.highlight(this.highlighted - 1);
    },
    highlightNext() {
      this.highlight(this.highlighted + 1);
    }
  },
  watch: {
    items(value) {
      if (this.isAsync) {
        this.results = value;
        this.isLoading = false;
        this.isOpen = true;
      }
    }
  }
};
</script>


<style>
.autocomplete {
  @apply .relative;
}
.autocomplete-input {
  @apply .block .appearance-none .border .rounded .w-full .py-2 .px-3
      .text-grey-darker .text-left .leading-tight .text-sm .cursor-pointer;
}
.autocomplete-input:focus {
  outline: 0;
  box-shadow: 0 0 5px #1f9d55;
}
.has-error .autocomplete-input {
  @apply .bg-red-lightest .border-red-light;
}
.has-error .autocomplete-input:focus {
  box-shadow: 0 0 5px #ef5753;
}
.autocomplete-dropdown {
  @apply .bg-white .shadow .rounded .absolute .pin-l .pin-r .z-50 .border .my-1 .p-2;
}
.autocomplete-results {
  @apply .relative .p-0;
  list-style: none;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  max-height: 14rem;
}
.autocomplete-result {
  @apply .cursor-pointer .rounded .text-grey-darker .text-sm;
  padding: 0.5rem 0.75rem;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.autocomplete-result:hover {
  @apply .bg-grey-lighter .text-green-dark;
}
.autocomplete-result.is-active,
.autocomplete-result.is-active:hover {
  @apply .bg-grey-lighter .text-green-dark;
}
</style>
